import { ReactComponent as EmptyPanelDroneIcon } from '@assets/components/panel/notification/empty-panel-drone-icon.svg';
import { Center, For, Spinner, Text, VStack } from '@chakra-ui/react';
import { CockpitPanelIntlKeysEnum } from '@components/cockpit/cockpit.types';
import CockpitDialog from '@components/cockpit/CockpitDialog';
import { CockpitListLayout } from '@components/cockpit/layouts/CockpitListLayout';
import ActivationAndFlightPlanRow from '@components/cockpit/panels/flightPlan/ActivationAndFlightPlanRow';
import FlightPlansActions from '@components/cockpit/panels/flightPlan/FlightPlansActions';
import Panel from '@components/cockpit/panels/Panel';
import { createToast, ToastStatusEnum } from '@components/ui/toaster';
import { useDeleteActivationMutation } from '@services/utm/activation.api';
import { createErrorMessage } from '@utils/common.utils';
import { FormattedMessage, useIntl } from 'react-intl';

import CockpitPanelHeader from '../CockpitPanelHeader';
import FlightPlanFilters from './FlightPlanFilters';
import FlightPlanFormContainer from './FlightPlanFormContainer';
import FlightPlansHeader from './FlightPlansHeader';
import { FlightPlansManagementContextProvider, useFlightPlansManagementContext } from './FlightPlansManagementContext';
import ActivationAndFlightPlanMixedView from './FlightPlanView';

function FlightPlanFallBack() {
  const { isLoadingGetAll } = useFlightPlansManagementContext();
  if (isLoadingGetAll) {
    return (
      <Center width="100%" height="100%">
        <Spinner color="neutral.200" width="100px" height="100px" />
      </Center>
    );
  }
  return (
    <Center width="100%" height="100%">
      <VStack gap={3}>
        <EmptyPanelDroneIcon />
        <Text
          textStyle="lg"
          fontFamily="space"
          fontFeatureSettings="'liga' off"
          color="neutral.200"
          textAlign="center"
          userSelect="none"
        >
          <FormattedMessage id="components.flightPlans.empty" />
        </Text>
      </VStack>
    </Center>
  );
}

function FlightPlans() {
  const { formatMessage } = useIntl();
  const {
    setEditedFlightPlanCode,
    view,
    activationAndFlightPlans,
    selectedForDeletionFlightPlan,
    setSelectedForDeletionFlightPlan,
    dispatch,
  } = useFlightPlansManagementContext();

  const [deleteFlightPlan, { isLoading }] = useDeleteActivationMutation();
  return (
    <CockpitListLayout
      dialog={
        <CockpitDialog
          open={selectedForDeletionFlightPlan !== null}
          isLoading={isLoading}
          onCancel={() => setSelectedForDeletionFlightPlan(null)}
          onConfirm={() => {
            if (!selectedForDeletionFlightPlan) {
              return;
            }
            deleteFlightPlan(selectedForDeletionFlightPlan.id)
              .unwrap()
              .then(() => {
                createToast(
                  formatMessage(
                    { id: 'components.flightPlans.message.delete' },
                    { name: selectedForDeletionFlightPlan.name },
                  ),
                  ToastStatusEnum.SUCCESS,
                );
                setSelectedForDeletionFlightPlan(null);
                dispatch({ type: 'DELETE', payload: selectedForDeletionFlightPlan.id });
              })
              .catch((error) => createToast(createErrorMessage(error), ToastStatusEnum.ERROR));
          }}
        >
          <Text color="neutral.100" width="100%" whiteSpace="pre-line">
            <FormattedMessage
              id="components.flightPlans.popup.descriptionDelete"
              values={{ name: selectedForDeletionFlightPlan?.name }}
            />
          </Text>
        </CockpitDialog>
      }
      actions={<FlightPlansActions onCreate={() => setEditedFlightPlanCode(undefined)} />}
      filters={view === 'list' && <FlightPlanFilters />}
      header={activationAndFlightPlans.length > 0 && view === 'list' && <FlightPlansHeader />}
    >
      {view === 'list' ? (
        <For each={activationAndFlightPlans} fallback={<FlightPlanFallBack />}>
          {(activationAndFlightPlan, index) => (
            <ActivationAndFlightPlanRow
              activationAndFlightPlan={activationAndFlightPlan}
              key={activationAndFlightPlan.activation.id}
              index={index}
            />
          )}
        </For>
      ) : (
        <ActivationAndFlightPlanMixedView />
      )}
    </CockpitListLayout>
  );
}

function FlightPlanManagementPanelContent() {
  const { editedFlightPlanCode } = useFlightPlansManagementContext();

  return <>{editedFlightPlanCode === null ? <FlightPlans /> : <FlightPlanFormContainer />}</>;
}

export function FlightPlanManagementPanel() {
  const { formatMessage } = useIntl();

  return (
    <FlightPlansManagementContextProvider>
      <Panel
        header={
          <CockpitPanelHeader
            label={formatMessage({ id: `components.sidebar.${CockpitPanelIntlKeysEnum.FLIGHT_PLANS}` })}
          />
        }
      >
        <FlightPlanManagementPanelContent />
      </Panel>
    </FlightPlansManagementContextProvider>
  );
}
